/* Stage 5 - B15 Shared Admin Configuration page.

   §19 Workstage Task Templates + §20 config home.  Closure Outcomes
   admin is B14 - we only surface a discoverability link here.

   Reuses the accepted admin shell.
*/

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { usePageMeta } from "../../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


function Card({ title, testid, children }) {
    return (
        <section className="bg-white border border-[#E5E2DC] rounded p-4"
                 data-testid={testid}>
            {title && <h2 className="font-manrope text-[15px] font-semibold text-charcoal mb-3"
                          data-testid={`${testid}-title`}>{title}</h2>}
            {children}
        </section>);
}


export default function AdminConfigPage() {
    usePageMeta({ title: "Configuration | Studio G4 Admin" });
    const [summary, setSummary] = useState(null);
    const [err, setErr] = useState("");
    const [savingSafety, setSavingSafety] = useState(false);
    const [safetyMsg, setSafetyMsg] = useState("");
    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/admin/config/summary`,
                                       { withCredentials: true });
            setSummary(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const toggleAutomaticReminders = async (next) => {
        setSavingSafety(true); setSafetyMsg("");
        try {
            await axios.patch(`${API}/admin/config/communications-safety`,
                { automatic_client_reminders: next },
                { withCredentials: true });
            setSafetyMsg(next
                ? "Automatic client reminders enabled."
                : "Automatic client reminders disabled.");
            await refresh();
        } catch (e) {
            setSafetyMsg(e?.response?.data?.error || e.message);
        } finally { setSavingSafety(false); }
    };

    if (err) return <div className="text-red-700 text-[13px]"
                          data-testid="config-error">{err}</div>;
    if (!summary) return <div className="text-[#666] text-[13px]"
                               data-testid="config-loading">Loading configuration…</div>;

    return (
        <div className="max-w-[1100px] mx-auto p-6 flex flex-col gap-5"
             data-testid="admin-config-page">
            <header>
                <h1 className="font-manrope text-3xl font-semibold text-charcoal mb-1"
                    data-testid="admin-config-title">Shared Admin Configuration</h1>
                <p className="text-[#666] text-[14px]">
                    Cross-workstage settings. Service Package configuration lives inside the
                    <Link to="/admin/service-packages"
                          className="text-[#A8543F] hover:underline mx-1"
                          data-testid="config-sp-link">Service Packages</Link>
                    catalogue.  Communication Content Library is a Phase C surface.
                </p>
            </header>

            <Card title="Workstage Task Templates" testid="config-tt-card">
                <p className="text-[12px] text-[#666] mb-3">
                    Default project tasks per Workstage.  Publishing a new version affects
                    <strong> future </strong>project Workstage instantiation only — existing
                    project task history is preserved.  Authority: A5-d.
                </p>
                <table className="w-full text-[13px]" data-testid="config-tt-table">
                    <thead className="bg-[#F5F3EE]">
                        <tr>
                            <th className="text-left px-3 py-1.5">Workstage</th>
                            <th className="text-left px-3 py-1.5">Current version</th>
                            <th className="text-left px-3 py-1.5">Items</th>
                            <th className="text-left px-3 py-1.5">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary.task_templates.workstages.map((row) => (
                            <tr key={row.workstage}
                                className="border-t border-[#E5E2DC]"
                                data-testid={`config-tt-row-ws${row.workstage}`}>
                                <td className="px-3 py-1.5">WS{row.workstage}</td>
                                <td className="px-3 py-1.5">
                                    {row.has_template ? `v${row.current_version}` : "—"}
                                </td>
                                <td className="px-3 py-1.5">{row.item_count}</td>
                                <td className="px-3 py-1.5">
                                    <Link to={`/admin/config/task-templates/ws${row.workstage}`}
                                          className="text-[#A8543F] hover:underline text-[12px]"
                                          data-testid={`config-tt-open-ws${row.workstage}`}>
                                        {row.has_template ? "Manage / Publish new version" : "Publish first version"}
                                    </Link>
                                </td>
                            </tr>))}
                    </tbody>
                </table>
            </Card>

            <Card title="Closure Outcomes" testid="config-co-card">
                <p className="text-[12px] text-[#666] mb-2">
                    Closure Outcome codes are managed by B14 at
                    <code className="mx-1 text-[11px] bg-[#F5F3EE] px-1 py-0.5 rounded">
                        /api/admin/closure-outcomes
                    </code>
                    (immutable codes, editable labels, activate/deactivate; hard-delete refused).
                </p>
                <p className="text-[13px]" data-testid="config-co-counts">
                    Active: <strong>{summary.closure_outcomes.active}</strong> ·
                    Total: <strong> {summary.closure_outcomes.total}</strong>
                </p>
            </Card>

            <Card title="Automatic client reminders" testid="config-safety-card">
                <p className="text-[12px] text-[#666] mb-3">
                    When enabled, the reminder system may automatically email
                    clients when configured reminders become due. Only
                    reminders using a Production communication template can be
                    sent. When disabled, reminder evaluation continues but
                    automatic client emails are not sent.
                </p>
                <div className="flex items-center gap-3">
                    <button type="button"
                        onClick={() => toggleAutomaticReminders(
                            !summary.communications_safety?.automatic_client_reminders)}
                        disabled={savingSafety}
                        aria-pressed={!!summary.communications_safety?.automatic_client_reminders}
                        data-testid="config-safety-reminders-toggle"
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded border text-[12px] font-semibold ${
                            summary.communications_safety?.automatic_client_reminders
                                ? "bg-[#345E34] text-white border-[#345E34]"
                                : "bg-white text-charcoal border-[#E5E2DC]"
                        }`}>
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                            summary.communications_safety?.automatic_client_reminders
                                ? "bg-white" : "bg-[#B0B0B0]"
                        }`} />
                        {summary.communications_safety?.automatic_client_reminders
                            ? "On" : "Off"}
                    </button>
                    <span className="text-[11px] text-[#666]"
                        data-testid="config-safety-reminders-state">
                        Current: {summary.communications_safety?.automatic_client_reminders
                            ? "ON" : "OFF (default)"}
                    </span>
                </div>
                {safetyMsg && (
                    <p className="text-[12px] text-[#666] mt-2"
                        data-testid="config-safety-msg">{safetyMsg}</p>)}
            </Card>

            <Card title="Everything else" testid="config-other-card">
                <ul className="text-[13px] text-[#333] list-disc pl-5 space-y-1">
                    <li data-testid="config-other-sp">
                        Service Package templates —
                        <Link to="/admin/service-packages"
                              className="text-[#A8543F] hover:underline ml-1"
                              data-testid="config-other-sp-link">/admin/service-packages</Link>
                    </li>
                    <li data-testid="config-other-clib">
                        Communication Content Library — deferred to Phase C (C8).
                    </li>
                    <li data-testid="config-other-portal">
                        Portal configuration — deferred to Phase C.
                    </li>
                </ul>
            </Card>
        </div>);
}


// --- WS Task Template detail ------------------------------------------------

export function WorkstageTaskTemplatePage({ ws }) {
    usePageMeta({ title: `WS${ws} task templates · Configuration | Studio G4 Admin` });
    const [current, setCurrent] = useState(null);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const load = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/config/task-templates/${ws}`,
                { withCredentials: true });
            setCurrent(r.data.current || null);
            setItems(r.data.current ? (r.data.current.items || []).map(i => ({
                key: i.key || "", title: i.title || "",
                order: i.order || 0,
                default_owner_role: i.default_owner_role || "",
                default_due_offset_days: i.default_due_offset_days || null,
                linked_programme_item_ref: i.linked_programme_item_ref || "",
                required_for_completion: !!i.required_for_completion,
                active: i.active !== false,
                note: i.note || "",
            })) : []);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [ws]);
    useEffect(() => { load(); }, [load]);

    const addItem = () => setItems(prev => [...prev, {
        key: "", title: "", order: prev.length + 1,
        default_owner_role: "", default_due_offset_days: null,
        linked_programme_item_ref: "",
        required_for_completion: false, active: true, note: "",
    }]);
    const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
    const patch = (idx, k, v) => setItems(prev =>
        prev.map((it, i) => i === idx ? { ...it, [k]: v } : it));

    const publish = async () => {
        setBusy(true); setErr(""); setMsg("");
        try {
            await axios.post(`${API}/admin/config/task-templates/publish`, {
                workstage: Number(ws),
                publication_op_id: `ui-${Date.now()}`,
                items: items.map(i => ({
                    ...i, default_due_offset_days:
                        i.default_due_offset_days === "" || i.default_due_offset_days === null
                        ? null : Number(i.default_due_offset_days),
                })),
            }, { withCredentials: true });
            setMsg("Published. Existing project tasks are not modified — only future instantiations use the new version.");
            await load();
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="max-w-[1100px] mx-auto p-6 flex flex-col gap-4"
             data-testid={`wstt-page-ws${ws}`}>
            <header className="flex items-center gap-3">
                <Link to="/admin/config" className="text-[13px] text-[#A8543F] hover:underline"
                      data-testid="wstt-back">← Configuration</Link>
                <h1 className="font-manrope text-2xl font-semibold text-charcoal"
                    data-testid="wstt-title">WS{ws} · Task Template</h1>
                {current && <span className="text-[12px] bg-[#F5F3EE] px-2 py-0.5 rounded"
                                    data-testid="wstt-current-version">Current v{current.version}</span>}
            </header>

            {err && <p className="text-red-700 text-[13px]" data-testid="wstt-error">{err}</p>}
            {msg && <p className="text-green-700 text-[13px]" data-testid="wstt-msg">{msg}</p>}

            <Card title="Template items (published as a new immutable version)" testid="wstt-items-card">
                <table className="w-full text-[13px]" data-testid="wstt-items-table">
                    <thead className="bg-[#F5F3EE]">
                        <tr>
                            <th className="text-left px-2 py-1.5">Order</th>
                            <th className="text-left px-2 py-1.5">Key</th>
                            <th className="text-left px-2 py-1.5">Title</th>
                            <th className="text-left px-2 py-1.5">Default owner (role)</th>
                            <th className="text-left px-2 py-1.5">Due offset (days)</th>
                            <th className="text-left px-2 py-1.5">Required</th>
                            <th className="text-left px-2 py-1.5">Active</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it, idx) => (
                            <tr key={idx} className="border-t border-[#E5E2DC]"
                                data-testid={`wstt-row-${idx}`}>
                                <td className="px-2 py-1">
                                    <input type="number" value={it.order}
                                           onChange={(e) => patch(idx, "order", Number(e.target.value))}
                                           className="w-14 border border-[#E5E2DC] rounded px-1 py-0.5"
                                           data-testid={`wstt-inp-order-${idx}`} />
                                </td>
                                <td className="px-2 py-1">
                                    <input value={it.key}
                                           onChange={(e) => patch(idx, "key", e.target.value)}
                                           className="w-full border border-[#E5E2DC] rounded px-1 py-0.5"
                                           data-testid={`wstt-inp-key-${idx}`} />
                                </td>
                                <td className="px-2 py-1">
                                    <input value={it.title}
                                           onChange={(e) => patch(idx, "title", e.target.value)}
                                           className="w-full border border-[#E5E2DC] rounded px-1 py-0.5"
                                           data-testid={`wstt-inp-title-${idx}`} />
                                </td>
                                <td className="px-2 py-1">
                                    <input value={it.default_owner_role || ""}
                                           onChange={(e) => patch(idx, "default_owner_role", e.target.value)}
                                           className="w-full border border-[#E5E2DC] rounded px-1 py-0.5"
                                           data-testid={`wstt-inp-owner-${idx}`} />
                                </td>
                                <td className="px-2 py-1">
                                    <input type="number" value={it.default_due_offset_days || ""}
                                           onChange={(e) => patch(idx, "default_due_offset_days", e.target.value)}
                                           className="w-20 border border-[#E5E2DC] rounded px-1 py-0.5"
                                           data-testid={`wstt-inp-due-${idx}`} />
                                </td>
                                <td className="px-2 py-1 text-center">
                                    <input type="checkbox" checked={!!it.required_for_completion}
                                           onChange={(e) => patch(idx, "required_for_completion", e.target.checked)}
                                           data-testid={`wstt-inp-req-${idx}`} />
                                </td>
                                <td className="px-2 py-1 text-center">
                                    <input type="checkbox" checked={!!it.active}
                                           onChange={(e) => patch(idx, "active", e.target.checked)}
                                           data-testid={`wstt-inp-active-${idx}`} />
                                </td>
                                <td className="px-2 py-1">
                                    <button type="button" onClick={() => removeItem(idx)}
                                            className="text-[11px] text-red-700 hover:underline"
                                            data-testid={`wstt-row-remove-${idx}`}>Remove</button>
                                </td>
                            </tr>))}
                    </tbody>
                </table>
                <div className="mt-3 flex justify-between">
                    <button type="button" onClick={addItem}
                            className="text-[12px] text-[#A8543F] hover:underline"
                            data-testid="wstt-row-add">+ Add item</button>
                    <button type="button" onClick={publish} disabled={busy}
                            className="bg-[#A8543F] text-white text-[13px] px-3 py-1.5 rounded"
                            data-testid="wstt-publish">
                        {busy ? "Publishing…" : "Publish new version"}
                    </button>
                </div>
            </Card>
        </div>);
}
